import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import {
  Coordinate,
  DistancePipe,
  GpsOnly,
  IconType,
  TrainingSection,
  WorkoutMode,
} from '@kinomap-dev/ng-ui-lib/core';
import { ContentType } from '../../pages/content-creation/content-creation';
import { contentTypeToIcon } from '../../utils/content-type-to-icon';
import { CreateContentService } from '../../services/create-content.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UploadService } from '../../services/upload-service';
import { Toaster } from '../../services/toaster';
import { IconComponent } from '@kinomap-dev/ng-ui-lib/basics';
import { MediaBlockService } from '../../services/media-block-service';
import { AnalyticsService } from '../../services/analytics.service';
import { LeafletDirective, LeafletModule } from '@bluehalo/ngx-leaflet';
import { LeafletService } from '../../services/leaflet';
import { Tooltip } from '../../directives/tooltip.directive';
import { ColorService } from '../../services/color.service';
import { UpperFirstLetterPipe } from '../../pipes/upper-first-letter-pipe';
import { Location } from '@angular/common';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';
import { ContentPreviewSource, ContentPreviewViewModel } from '../../types/content-preview.type';
import {
  getContentTypeFromContent,
  getPreviewFromContent,
  getPreviewFromCreationForm,
} from '../../utils/map-content-preview-data';
import { Chip } from '../chip/chip';
import { Observable } from 'rxjs';
import { WorkoutPreviewChart } from '../workout-preview-chart/workout-preview-chart';
import { SectionModelType } from '../../types/section.type';
import { SectionsEditorService } from '../../services/sections-editor.service';
import { WorkoutDifficultyLevel } from '../../types/workout-difficulty.type';
import { DIFFICULTY_LEVEL_COLORS } from '../../configs/sections-editor/workout-mode-colors.config';
import { WORKOUT_MODE_LABELS } from '../../configs/sections-editor/workout-mode-labels.config';
import { getAverageDifficultyLevel } from '../../utils/section-difficulty.utils';
import { Calories, generateCalories } from '../../utils/calories.helpers';
import {
  DEFAULT_PREVIEW_CHIPS,
  PREVIEW_CHIP_ICONS,
  PREVIEW_CHIPS_BY_CONTENT_TYPE,
  PreviewChipData,
} from '../../configs/content-preview/preview-chips.config';

type DifficultySource = { sections: SectionModelType[]; mode: WorkoutMode | undefined };

@Component({
  selector: 'app-content-preview',
  imports: [
    IconComponent,
    DatePipe,
    TranslateModule,
    LeafletDirective,
    LeafletModule,
    Tooltip,
    UpperFirstLetterPipe,
    Chip,
    WorkoutPreviewChart,
  ],
  templateUrl: './content-preview.html',
  styleUrl: './content-preview.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [LeafletService],
  host: {
    '[class.creation]': 'mode()',
  },
})
export class ContentPreview {
  public readonly colorService: ColorService = inject(ColorService);
  public readonly leafletService: LeafletService = inject(LeafletService);
  public readonly mediaBlockService: MediaBlockService = inject(MediaBlockService);
  public readonly colorsService: ColorService = inject(ColorService);
  public readonly location: Location = inject(Location);
  private router: Router = inject(Router);
  private readonly sectionsEditorService: SectionsEditorService = inject(SectionsEditorService);

  private readonly toaster: Toaster = inject(Toaster);
  private readonly analyticsService: AnalyticsService = inject(AnalyticsService);
  private readonly translateService: TranslateService = inject(TranslateService);
  public createContentService: CreateContentService = inject(CreateContentService);
  private uploadService: UploadService = inject(UploadService);

  readonly contentType: InputSignal<ContentType | undefined> = input<ContentType | undefined>();
  readonly content: InputSignal<ContentPreviewSource | undefined> = input<
    ContentPreviewSource | undefined
  >();
  readonly mode: InputSignal<'creation' | undefined> = input();
  readonly previewContentType: Signal<ContentType | undefined> = computed(() => {
    return (
      this.contentType() ??
      getContentTypeFromContent(this.content()?.type || this.mediaBlockService.contentType)
    );
  });
  readonly preview: Signal<ContentPreviewViewModel> = computed(() => {
    const content: ContentPreviewSource | undefined = this.content();

    return content
      ? getPreviewFromContent(content)
      : getPreviewFromCreationForm(
          this.createContentService,
          this.mediaBlockService,
          this.previewContentType(),
        );
  });

  showFooter: InputSignal<boolean> = input(true);
  showData: InputSignal<boolean> = input(true);

  onSubmitForm: OutputEmitterRef<void> = output<void>();
  onSaveForm: OutputEmitterRef<void> = output<void>();

  readonly isEditing: Signal<boolean> = computed(
    () => this.createContentService.editedContentId() !== undefined,
  );

  neutral300: string = '#B2B3B5';

  trainingSections: WritableSignal<TrainingSection[]> = signal<TrainingSection[]>([]);
  information: WritableSignal<GpsOnly> = signal<GpsOnly>({} as GpsOnly);
  coordinates: WritableSignal<Coordinate[]> = signal<Coordinate[]>(
    this.createContentService.coordinates(),
  );

  readonly workoutSections: Signal<SectionModelType[]> = computed(() =>
    this.content() || this.previewContentType() !== 'workout'
      ? []
      : this.createContentService.workoutSections(),
  );

  readonly workoutDuration: Signal<number> = computed(() =>
    this.workoutSections().reduce(
      (total: number, section: SectionModelType) =>
        total + section.duration.minutes * 60 + section.duration.seconds,
      0,
    ),
  );

  /**
   * Sections et mode servant au calcul de la difficulte : celles du workout, ou les
   * intervalles du coaching. Rien pour les autres contenus ni pour un contenu existant.
   */
  private readonly difficultySource: Signal<DifficultySource | undefined> = computed(() => {
    if (this.content()) return undefined;
    switch (this.previewContentType()) {
      case 'workout':
        return {
          sections: this.createContentService.workoutSections(),
          mode: this.createContentService.workoutMode(),
        };
      case 'coaching':
        return {
          sections: this.createContentService.coachingSections(),
          mode: this.createContentService.coachingWorkoutMode(),
        };
      default:
        return undefined;
    }
  });

  readonly workoutDifficulty: Signal<WorkoutDifficultyLevel | undefined> = computed(() => {
    const source: DifficultySource | undefined = this.difficultySource();
    if (!source?.mode) return undefined;
    const { sections, mode }: DifficultySource = source;
    return getAverageDifficultyLevel(
      sections.map((section: SectionModelType) => ({
        datas: this.sectionsEditorService.sectionDatas(section, mode),
        duration: section.duration.minutes * 60 + section.duration.seconds,
      })),
    );
  });

  readonly workoutDifficultyColor: Signal<string | undefined> = computed(() => {
    const level: WorkoutDifficultyLevel | undefined = this.workoutDifficulty();
    return level ? DIFFICULTY_LEVEL_COLORS[level] : '#b2b3b5';
  });

  private readonly distancePipe: DistancePipe = new DistancePipe();

  readonly chips: Signal<{ data: PreviewChipData; icon: IconType; text: string }[]> = computed(
    () => {
      const contentType: ContentType | undefined = this.previewContentType();
      const chipsData: readonly PreviewChipData[] =
        (contentType && PREVIEW_CHIPS_BY_CONTENT_TYPE[contentType]) ?? DEFAULT_PREVIEW_CHIPS;

      return chipsData.map((data: PreviewChipData) => ({
        data,
        icon: PREVIEW_CHIP_ICONS[data],
        text: this.chipText(data) ?? '-',
      }));
    },
  );

  private creationSport(): string | undefined {
    switch (this.previewContentType()) {
      case 'workout':
        return this.createContentService.workoutSport();
      case 'coaching':
        return this.createContentService.coachingSport();
      default:
        return this.createContentService.sport();
    }
  }

  private chipText(data: PreviewChipData): string | undefined {
    const preview: ContentPreviewViewModel = this.preview();
    switch (data) {
      case 'duration': {
        const duration: number | undefined =
          this.previewContentType() === 'workout' ? this.workoutDuration() : preview.duration;
        if (!duration) return undefined;
        const totalSeconds: number = Math.round(duration);
        return `${Math.round(totalSeconds / 60)}min`;
      }
      case 'distance':
        return preview.distance ? this.distancePipe.transform(preview.distance) : undefined;
      case 'positiveSlope':
        return preview.positiveSlope
          ? this.distancePipe.transform(preview.positiveSlope)
          : undefined;
      case 'workoutMode': {
        const workoutMode: WorkoutMode | undefined = this.createContentService.workoutMode();
        if (!workoutMode) return undefined;
        return WORKOUT_MODE_LABELS[workoutMode];
      }
      case 'calories': {
        const duration: number | undefined = this.workoutSections().length
          ? this.workoutDuration()
          : preview.duration;
        if (!duration) return undefined;
        const content: ContentPreviewSource | undefined = this.content();
        const sport: string | undefined =
          content && 'sport' in content ? (content.sport ?? undefined) : this.creationSport();

        const calories: Calories = generateCalories(sport, duration);
        return `${calories.value}${calories.unit}`;
      }
    }
  }

  readonly visibilityLabelKey: Signal<string | undefined> = computed(() => {
    const visibility: string | undefined = this.preview().visibility;
    if (!visibility) return undefined;
    return `publication_block.visibility.${visibility === 'onlyFollower' ? 'community' : visibility}`;
  });

  readonly showWorkoutChart: Signal<boolean> = computed(
    () =>
      this.previewContentType() === 'workout' &&
      !this.preview().thumbnailUrl &&
      this.workoutSections().length > 0,
  );

  readonly hasCoordinatesForGpsOnly: Signal<boolean> = computed(
    () => this.preview().hasCoordinatesForGpsOnly,
  );

  get formIsValid() {
    switch (this.previewContentType()) {
      case 'gps-only':
        return this.createContentService.gpsTrackFormIsValid();
      case 'coaching':
        return this.createContentService.coachingFormIsValid();
      case 'scenic':
        return this.createContentService.scenicFormIsValid();
      case 'workout':
        return this.createContentService.workoutFormIsValid();
      default:
        return undefined;
    }
  }

  sendContent(): void {
    if (this.formIsValid && this.isEditing()) {
      this.onSaveForm.emit();
    } else if (this.formIsValid) {
      let formSender: Observable<boolean> | undefined;
      switch (this.contentType()) {
        case 'gps-only':
          formSender = this.uploadService.gpsOnly();
          break;
        case 'workout':
          formSender = this.uploadService.workout();
          break;
        case 'coaching':
          formSender = this.uploadService.coaching();
          break;
        default:
          formSender = this.uploadService.scenic();
      }

      if (formSender) {
        formSender.subscribe({
          next: (value: boolean) => {
            if (value) {
              this.analyticsService.track('upload_start', {
                content_type: this.contentType() ?? 'scenic',
              });
              this.router.navigate(['content-creation/upload']);
            } else {
              this.analyticsService.track('upload_failed', {
                content_type: this.contentType() ?? 'scenic',
                step: 'api',
              });
              this.toaster.error(
                this.translateService.instant('content_creation_preview.errors.title'),
                this.translateService.instant('content_creation_preview.errors.upload_failed'),
                'warning',
              );
            }
          },
        });
      } else {
        console.error("Form Sender can't find form to send !");
      }
    } else {
      this.toaster.error(
        undefined,
        this.translateService.instant('content_creation_preview.errors.required_fields'),
        'warning',
      );
    }
    this.onSubmitForm.emit();
  }

  get icon(): IconType | undefined {
    if (!this.previewContentType()) return undefined;
    return contentTypeToIcon(this.previewContentType()!);
  }

  get syncroStatusColor(): string {
    switch (this.mediaBlockService.syncroStatus()) {
      case 'complete':
        return this.colorsService.success() ?? '#6BB840';
      case 'no_sync':
        return this.colorsService.danger500() ?? '#FF2929';
      case 'in_progress':
        return this.colorsService.brandSecondary() ?? '#428FF3';
      default:
        return this.colorsService.textColor() ?? '#191A1A';
    }
  }

  viewOnKinomapClick(): void {
    const kinomapContentUrl: string = environment.website;
    window.open(kinomapContentUrl, '_blank');
  }
}
